<template>
  <div class="image-response-details" :class="{
    'with-hover': withHover,
    'hover-disabled': disableHover
  }" ref="container" @mouseenter="showPopover" @mouseleave="startHideTimer">
    <PreviewSection
      v-if="withHover"
      :selectedCount="selectedCount"
      :selectedLabels="selectedLabels"
      @show="showPopover"
      @hide="startHideTimer"
    />

    <div class="hover-content"
         :class="{ 'visible': isVisible }"
         ref="popover"
         @mouseenter="cancelHideTimer"
         @mouseleave="startHideTimer">
      <SubjectsGrid
        :subjects="groupedOptions"
        :imageSize="30"
        :enableZoom="shouldEnableZoom"
      />
    </div>
  </div>
</template>

<script>
import PreviewSection from './components/PreviewSection.vue'
import SubjectsGrid from './components/SubjectsGrid.vue'

export default {
  name: 'ImageResponseDetails',
  components: {
    PreviewSection,
    SubjectsGrid
  },

  props: {
    response: {
      type: Object,
      required: true
    },
    survey: {
      type: Object,
      required: true
    },
    withHover: {
      type: Boolean,
      default: false
    },
    disableHover: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isVisible: false,
      hideTimer: null
    }
  },

  computed: {
    selectedCount () {
      return this.getSelectedOptions(this.response).length
    },

    selectedLabels () {
      const selectedOptions = this.getSelectedOptions(this.response)
      const labels = selectedOptions.slice(0, 2).map(item => item.value).join(', ')
      return selectedOptions.length > 2 ? `${labels}...` : labels
    },

    groupedOptions () {
      return this.getGroupedImageOptions(this.response)
    },

    shouldEnableZoom () {
      return this.$parent.$options.name === 'DetailResponse'
    }
  },

  methods: {
    getSelectedOptions (response) {
      if (!response.formattedValue || !Array.isArray(response.formattedValue)) return []
      return response.formattedValue.filter(item =>
        item.optionId &&
        item.value &&
        !item.value.includes('Which of the following')
      )
    },

    findSurveyQuestion (questionId) {
      const question = this.survey.questions?.find(q => {
        return q?._id === questionId || q?.question?._id === questionId
      })
      return question?.question?._id ? question?.question : question
    },

    getSubjectSelectedOptions (subject, selectedOptionsMap) {
      return (subject.options || [])
        .reduce((selected, option) => {
          const selectedOption = selectedOptionsMap.get(option._id)
          if (selectedOption) {
            selected.push({
              optionId: option._id,
              value: selectedOption.value,
              imageUrl: option.label
            })
          }
          return selected
        }, [])
    },

    getGroupedImageOptions (response) {
      const selectedOptions = this.getSelectedOptions(response)
      if (selectedOptions.length === 0) return []

      const surveyQuestion = this.findSurveyQuestion(response.question._id)
      if (!surveyQuestion?.subjects) return []

      const selectedOptionsMap = new Map(
        selectedOptions.map(option => [option.optionId, option])
      )

      return surveyQuestion.subjects
        .reduce((groupedSubjects, subject) => {
          const subjectSelectedOptions = this.getSubjectSelectedOptions(subject, selectedOptionsMap)
          if (subjectSelectedOptions.length > 0) {
            groupedSubjects.push({
              id: subject._id,
              label: subject.label,
              options: subjectSelectedOptions
            })
          }
          return groupedSubjects
        }, [])
    },

    // Timer methods
    startHideTimer () {
      if (this.hideTimer) clearTimeout(this.hideTimer)
      this.hideTimer = setTimeout(() => {
        this.isVisible = false
      }, 1000)
    },

    cancelHideTimer () {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer)
        this.hideTimer = null
      }
    },

    showPopover () {
      this.isVisible = true
    }
  },

  beforeDestroy () {
    this.cancelHideTimer()
  }
}
</script>

<style lang="scss">
.image-response-details {
  width: 100%;
  position: relative;

  &.hover-disabled {
    pointer-events: none;
    &:hover .hover-content {
      display: none;
    }
  }

  &.with-hover {
    position: relative;

    .hover-content {
      position: absolute;
      bottom: calc(100% + 8px);
      left: 50%;
      transform: translateX(-50%);
      width: 400px;
      max-width: calc(100vw - 32px);
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      z-index: 1000;
      padding: 16px;
      border: 1px solid #e1e1e1;
      max-height: 40vh;
      overflow-y: auto;
      overflow-x: hidden;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.3s ease, visibility 0.3s ease;

      &:after {
        display: none;
      }

      &.visible {
        opacity: 1;
        visibility: visible;
      }
    }

    &:hover .hover-content {
      display: block;
      @media (max-height: 500px) {
        bottom: auto;
        top: calc(100% + 8px);

        &:after {
          bottom: auto;
          top: -6px;
          border: none;
          border-left: 1px solid #e1e1e1;
          border-top: 1px solid #e1e1e1;
        }
      }
    }
  }

  // Transition classes
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(10px);
  }
}
</style>
